/**
 * Which tabs the Invite Client dialog shows, resolved once and shared: the strip needs it to
 * render and the dialog needs the count to size itself. Computed twice, they would disagree.
 */

import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import {
  useIsPilotRestricted,
  usePilotAgentEnteredIntake,
} from '@/shared/hooks/use-active-pilot-program';
import { useIsPerClientInviteLinkEnabled } from '@/shared/hooks/use-per-client-invite-link';
import { useInviteClientTrigger, type TabType } from '@/features/invite-client';

export interface InviteClientTabsState {
  allowedTabs: TabType[];
  /** The tab actually rendered, after clamping a stale deep link back to a permitted one. */
  resolvedTab: TabType;
  /** False only when app config hides email, while pilot restrictions remain visible-disabled. */
  isEmailTabVisible: boolean;
  isEmailTabAvailable: boolean;
  /** Prefill remains available independently of the email invite capability. */
  isPrefillTabAvailable: boolean;
  hasAgentEnteredIntake: boolean;
  showFillFormTab: boolean;
  /** True when only the share-a-link tab should render, with no tab strip at all. */
  isLinkOnly: boolean;
}

export function useInviteClientTabs(): InviteClientTabsState {
  const { flags } = useFeatureFlags();
  const { inviteClientTab } = useInviteClientTrigger();
  const isEdjPilot = useIsEdjPilotAdvisor();
  // Re-opens "Fill Intake Form" behind the consent gate WITHOUT widening the program's
  // restrictions: the capability stays restricted, so nothing else about it changes.
  const hasAgentEnteredIntake = !!usePilotAgentEnteredIntake();
  const isInviteClientRestricted = useIsPilotRestricted('invite-client');
  const isIntakeFormRestricted = useIsPilotRestricted('intake-form');
  const isPrefillTabAvailable = useIsPerClientInviteLinkEnabled();

  const isEmailTabVisible = !flags?.hideInviteEmailAndFillIntakeForm || isEdjPilot;
  const isEmailTabAvailable = isEmailTabVisible && !isInviteClientRestricted;
  const isLinkOnly = !isEmailTabVisible && !isPrefillTabAvailable;

  // `isIntakeFormRestricted` stays true for agent-entered intake. The consent flow, not a relaxed
  // restriction, is what puts the existing Fill Intake Form tab back.
  const showFillFormTab = hasAgentEnteredIntake || !isIntakeFormRestricted;

  const allowedTabs: TabType[] = [
    'link',
    ...(isEmailTabAvailable ? (['email'] as TabType[]) : []),
    ...(isPrefillTabAvailable ? (['prefill'] as TabType[]) : []),
    ...(showFillFormTab ? (['fill-form'] as TabType[]) : []),
  ];

  const defaultTab: TabType = isEmailTabAvailable
    ? 'email'
    : isPrefillTabAvailable
      ? 'prefill'
      : 'link';
  const currentTab = inviteClientTab || defaultTab;

  const resolvedTab = allowedTabs.includes(currentTab) ? currentTab : defaultTab;

  return {
    allowedTabs,
    resolvedTab,
    isEmailTabVisible,
    isEmailTabAvailable,
    isPrefillTabAvailable,
    hasAgentEnteredIntake,
    showFillFormTab,
    isLinkOnly,
  };
}
